import { Component, Input, OnInit } from '@angular/core';
import {
    FormBuilder,
    FormGroup,
    Validators,
    AbstractControl,
    ValidationErrors,
} from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { RegistroService } from 'src/app/registro.service';
import { GobiernoUser, Rol } from '../../interfaces/user.interface';
import { UserServiceService } from '../../services/user-service.service';
import Swal from 'sweetalert2';

export interface Usuario {
    nombre: string;
    email: string;
    rol?: string;
    fechaInactivacion?: string; // formato YYYY-MM-DD
}

function fechaSuperiorAHoy(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const seleccionada = toFechaLocal(control.value);
    if (!seleccionada) return null;
    return seleccionada > hoy ? null : { fechaInvalida: true };
}

function toFechaLocal(valor: Date | string): Date | null {
    if (valor instanceof Date && !Number.isNaN(valor.getTime())) {
        return new Date(valor.getFullYear(), valor.getMonth(), valor.getDate());
    }
    if (typeof valor !== 'string' || !valor) {
        return null;
    }
    const [anio, mes, dia] = valor.split('T')[0].split('-').map(Number);
    if (!anio || !mes || !dia) {
        return null;
    }
    return new Date(anio, mes - 1, dia);
}

function formatearFechaIso(fecha: Date | string): string {
    const local = toFechaLocal(fecha);
    if (!local) {
        return '';
    }
    const anio = local.getFullYear();
    const mes = String(local.getMonth() + 1).padStart(2, '0');
    const dia = String(local.getDate()).padStart(2, '0');
    return `${anio}-${mes}-${dia}`;
}

@Component({
    selector: 'app-edit-rol-modal',
    templateUrl: './edit-rol-modal.component.html',
    styleUrls: ['./edit-rol-modal.component.css'],
})
export class EditRolModalComponent implements OnInit {
    /** Usuario cuyos datos se van a editar */
    @Input() public usuario!: GobiernoUser;

    /**
     * Rol del usuario autenticado en sesión.
     * Solo 'admin-usuarios' puede guardar cambios.
     */
    @Input() public rolUsuarioActual = '';
    @Input() public roles: Rol[] = [];
    private readonly _rolesExcluidos = [
        'usuario_obligado_a_reportar_comercializador',
        'usuario_obligado_a_reportar_productor',
        'usuario_sin_registrar',
    ];

    public form!: FormGroup;
    public fechaMinima = new Date();
    public guardando = false;

    public constructor(
        public activeModal: NgbActiveModal,
        private fb: FormBuilder,
        private registroService: RegistroService,
        private userService: UserServiceService
    ) {}

    public ngOnInit(): void {
        const rolesFiltrados = this.roles.filter(
            (rol) => !this._rolesExcluidos.includes(rol.codigo ?? '')
        );
        
        rolesFiltrados.sort((a, b) =>
            (a.nombre ?? '').localeCompare(b.nombre ?? '', 'es')
        );
        
        this.roles = rolesFiltrados;
        const manana = new Date();
        manana.setHours(0, 0, 0, 0);
        manana.setDate(manana.getDate() + 1);
        this.fechaMinima = manana;

        this.form = this.fb.group({
            rol: [
                {
                    value: this.usuario?.roles?.[0]?.id ?? '',
                    disabled: !this.puedeEditar(),
                },
                [Validators.required],
            ],
            fechaInactivacion: [
                {
                    value: this.obtenerFechaFinRol(),
                    disabled: !this.puedeEditar(),
                },
                [Validators.required, fechaSuperiorAHoy],
            ],
        });
    }

    /** Devuelve si el dominio del correo es permitido para asignar rol */
    public get dominioValido(): boolean {
        return (
            this.usuario?.entidad !== undefined &&
            this.usuario?.entidad !== null
        );
    }

    /** Devuelve si el usuario en sesión tiene permiso para editar */
    public get tienePermiso(): boolean {
        return true; //this.registroService.permisos && (this.registroService.permisos as string[]).includes('admin-usuarios');
    }

    public get fechaError(): string {
        if (this.form?.get('fechaInactivacion')?.errors?.['fechaInvalida']) {
            return 'La fecha de inactivación debe ser superior a la fecha actual.';
        }
        return 'Este campo es obligatorio.';
    }

    /** Combina ambas condiciones */
    private puedeEditar(): boolean {
        return this.dominioValido && this.tienePermiso;
    }

    /** Obtiene fechaFin del rol activo en formato YYYY-MM-DD para el input date */
    private obtenerFechaFinRol(): string {
        const fechaFin = this.usuario?.roles?.[0]?.UsuarioRol?.fechaFin;
        if (!fechaFin) {
            return '';
        }
        return fechaFin.split('T')[0];
    }

    /** Extrae iniciales del nombre para el avatar */
    public getInitials(): string {
        if (!this.usuario?.nombre) return '?';
        const partes = this.usuario.nombre.trim().split(' ');
        const inicial1 = partes[0]?.[0] || '';
        const inicial2 = partes[1]?.[0] || '';
        return (inicial1 + inicial2).toUpperCase();
    }

    /** Cierra sin guardar (botón X o botón Cerrar) */
    public cerrarSinGuardar(): void {
        this.activeModal.dismiss('sin-cambios');
    }

    /** Guarda y cierra si el formulario es válido */
    public confirmar(): void {
        if (this.form.invalid || !this.puedeEditar() || this.guardando) {
            this.form.markAllAsTouched();
            return;
        }

        const { rol, fechaInactivacion } = this.form.getRawValue();

        this.guardando = true;
        this.userService
            .asignarRol({
                usuarioId: String(this.usuario.id),
                rolId: String(rol),
                estado: true,
                fechaInicio: null,
                fechaFin: `${formatearFechaIso(fechaInactivacion)}T00:00:00.000Z`,
            })
            .subscribe({
                next: () => {
                    Swal.fire({
                        title: 'Rol asignado correctamente',
                        text: 'El rol se ha asignado correctamente',
                        icon: 'success',
                    });
                    this.activeModal.close({
                        ...this.usuario,
                        rolId: rol,
                        fechaInactivacion,
                    });
                },
                error: (error) => {
                    this.guardando = false;
                    Swal.fire({
                        title: 'Error',
                        text:
                            error?.error?.message ?? 'Error al asignar el rol',
                        icon: 'error',
                    });
                },
            });
    }
}
